import React from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, TrashIcon, PencilIcon, PlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { TrophyIcon as TrophyIconSolid } from '@heroicons/react/24/solid';
import { Player, PlayerWithLeagueContext, RatingHistory, LeagueOutput } from '../../generated-api';
import PlayerAvatar from './PlayerAvatar';
import Tooltip from './Tooltip';
import ConservativeRatingDisplay from './ConservativeRatingDisplay';
import { comparePlayersByRank, CONSERVATIVE_RATING_EXPLANATION } from '../../utils/playerRanking';
import { resolveLeagueIcon } from '../../utils/leagueIcon';

interface PlayerListProps {
  players: (Player | PlayerWithLeagueContext)[];
  playerHistories?: Record<string, RatingHistory>;
  playerLeaguesMap?: Record<string, LeagueOutput[]>;
  showActions?: boolean;
  showCreateButton?: boolean;
  maxItems?: number;
  showOnlyActivePlayers?: boolean;
  showRanking?: boolean;
  onCreatePlayer?: () => void;
  onDeletePlayer?: (playerId: string, playerName: string) => void;
}

const PlayerList: React.FC<PlayerListProps> = ({
  players,
  playerHistories = {},
  playerLeaguesMap = {},
  showActions = false,
  showCreateButton = false,
  maxItems,
  showOnlyActivePlayers = false,
  showRanking = true,
  onCreatePlayer,
  onDeletePlayer,
}) => {
  const getLatestRatingInfo = (player: Player | PlayerWithLeagueContext) => {
    const history = playerHistories[player.id];
    if (history && history.ratingProgression && history.ratingProgression.length > 0) {
      const lastEntry = history.ratingProgression[history.ratingProgression.length - 1];
      return { rating: lastEntry.ratingAfter.rating, sigma: lastEntry.ratingAfter.sigma };
    }
    if ('leagueRating' in player && player.leagueRating) {
      return { rating: player.leagueRating.rating, sigma: player.leagueRating.sigma };
    }
    return { rating: 25.0, sigma: 8.33 };
  };

  const isPlayerRatingUncertain = (player: Player | PlayerWithLeagueContext, gamesPlayed: number, sigma: number): boolean => {
    // First check if player has leagueRating with isRatingUncertain flag
    if ('leagueRating' in player && player.leagueRating) {
      return player.leagueRating.isRatingUncertain;
    }
    // Otherwise calculate from gamesPlayed and sigma
    return (gamesPlayed < 5) || (sigma > 5);
  };

  const getGamesPlayed = (player: Player | PlayerWithLeagueContext): number =>
    playerHistories[player.id]?.ratingProgression?.length ||
    ('leagueRating' in player ? player.leagueRating?.gamesPlayed : 0) ||
    0;

  // One rule everywhere: conservative rating (mu - 3 sigma), highest first
  const sortedPlayers = [...players].sort((a, b) =>
    comparePlayersByRank(
      { name: a.name, ...getLatestRatingInfo(a), gamesPlayed: getGamesPlayed(a) },
      { name: b.name, ...getLatestRatingInfo(b), gamesPlayed: getGamesPlayed(b) },
    ),
  );

  const displayPlayers = maxItems ? sortedPlayers.slice(0, maxItems) : sortedPlayers;
  const highestRatedPlayer = sortedPlayers[0];

  if (players.length === 0) {
    return (
      <div className="bg-surface shadow rounded-xl p-12 text-center">
        <UserIcon className="mx-auto h-12 w-12 text-disabled" />
        <h3 className="mt-2 text-sm font-medium text-heading">No players</h3>
        <p className="mt-1 text-sm text-muted">Get started by creating your first player.</p>
        {showCreateButton && onCreatePlayer && (
          <div className="mt-6">
            <button
              onClick={onCreatePlayer}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Player
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-surface shadow overflow-hidden sm:rounded-md">
      {showRanking && (
        <p className="px-4 py-2 sm:px-6 text-xs text-muted border-b border-edge" data-testid="rating-explanation">
          <strong className="font-medium text-body">Rating:</strong> {CONSERVATIVE_RATING_EXPLANATION}
        </p>
      )}
      <ul className="divide-y divide-edge">
        {displayPlayers.map((player, index) => {
          const playerHistory = playerHistories[player.id];
          const gamesPlayed = playerHistory?.ratingProgression?.length || 0;
          const { rating, sigma } = getLatestRatingInfo(player);
          const isUncertain = isPlayerRatingUncertain(player, gamesPlayed, sigma);

          return (
            <li key={player.id}>
              <Link
                data-track="player_card"
                to={`/players/${player.id}`}
                className="block active:scale-[0.98] transition-transform"
                data-testid={`player-link-${player.id}`}
              >
                <div className={`px-4 py-4 sm:px-6 transition-colors ${
                  isUncertain
                    ? 'bg-surface-subtle/50 hover:bg-surface-active/50'
                    : 'hover:bg-surface-hover'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-16 w-16 relative">
                        <PlayerAvatar name={player.name} imageUrl={player.imageUrl} size="medium" />
                        {highestRatedPlayer && player.id === highestRatedPlayer.id && (
                          <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1 shadow-md">
                            <TrophyIconSolid className="h-4 w-4 text-yellow-800" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <h3 className="text-sm font-medium text-heading hover:text-primary-700 transition-colors">
                            {player.name}
                          </h3>
                          {isUncertain && (
                            <Tooltip
                              trigger={
                                <InformationCircleIcon 
                                  className="h-3.5 w-3.5 text-disabled cursor-help ml-1 flex-shrink-0" 
                                />
                              }
                              content={
                                <p className="leading-relaxed">
                                  Rating is uncertain: player has played fewer than 5 games or has high uncertainty (σ &gt; 5)
                                </p>
                              }
                              hover={true}
                              showCloseButton={false}
                              width="w-72"
                            />
                          )}
                          {showRanking && (
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              index === 0
                                ? 'bg-amber-100 text-amber-900'
                                : index === 1
                                ? 'bg-stone-200 text-stone-700'
                                : index === 2
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-stone-100 text-stone-600'
                            }`}>
                              #{index + 1}
                            </span>
                          )}
                        </div>
                        <div className="mt-1">
                          <p className="text-sm font-medium">
                            <ConservativeRatingDisplay rating={rating} sigma={sigma} isUncertain={isUncertain} />
                          </p>
                          <p className="text-xs text-muted">
                            {gamesPlayed} game{gamesPlayed !== 1 ? 's' : ''} played
                          </p>
                          {playerLeaguesMap[player.id] && playerLeaguesMap[player.id].length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-x-1 gap-y-1 sm:gap-1">
                              {playerLeaguesMap[player.id]
                                .filter(league => !league.isGlobal && league.name !== 'Unranked' && !league.isGameSpecificLeague)
                                .map(league => (
                                  <button
                                    data-track="player_league_chip"
                                    key={league.id}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // Use window.location for navigation to avoid nested links
                                      window.location.href = `/leagues/${league.id}`;
                                    }}
                                    className="group inline-flex items-center min-h-[44px] -my-2.5 sm:min-h-0 sm:my-0 cursor-pointer"
                                  >
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 group-hover:bg-purple-200 transition-colors">
                                      <span className="mr-1">
                                        {resolveLeagueIcon(league.icon)}
                                      </span>
                                      {league.name}
                                    </span>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {showActions && onDeletePlayer && (
                      <div className="flex items-center space-x-4 -mr-3 sm:mr-0" onClick={(e) => e.preventDefault()}>
                        <div className="flex flex-col sm:flex-row sm:space-x-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            aria-label={`Edit ${player.name}`}
                            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-md text-primary-700 hover:text-primary-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            data-track="player_delete"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onDeletePlayer(player.id, player.name);
                            }}
                            aria-label={`Delete ${player.name}`}
                            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-md text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PlayerList;